import { useEffect, useRef, useCallback } from 'react';
import {
  CompleteFormData,
  AggregatedFormData,
  PerFormData,
  FormDataSnapshot,
} from '../types/formTypes';

interface UseFormDataAggregatorProps {
  initialFormData: CompleteFormData[];
  onFormDataChange?: (formData: AggregatedFormData) => void;
  onFormChange?: (formId: string, formData: PerFormData) => void;
  formDataRef?: React.MutableRefObject<AggregatedFormData | null>;
  formDataMap: Map<string, FormDataSnapshot>;
}

export const useFormDataAggregator = ({
  initialFormData,
  onFormDataChange,
  onFormChange,
  formDataRef,
  formDataMap,
}: UseFormDataAggregatorProps) => {
  const prevFormDataMapRef = useRef<Map<string, FormDataSnapshot>>(new Map());

  const aggregateFormData = useCallback((): AggregatedFormData => {
    const forms: FormDataSnapshot[] = [];
    
    // Iterate through initial form data to maintain order
    initialFormData.forEach(form => {
      const snapshot = formDataMap.get(form.form_id);
      if (snapshot) {
        forms.push(snapshot);
      } else {
        // If no tracked data yet, use initial data
        forms.push({
          form_id: form.form_id,
          form_name: form.form_name,
          description: form.description,
          fields: form.fields,
        });
      }
    });

    return { forms };
  }, [initialFormData, formDataMap]);

  useEffect(() => {
    // Check if any form data has changed
    let hasChanged = false;
    
    if (formDataMap.size !== prevFormDataMapRef.current.size) {
      hasChanged = true;
    } else {
      for (const [formId, currentSnapshot] of formDataMap.entries()) {
        const prevSnapshot = prevFormDataMapRef.current.get(formId);
        
        if (!prevSnapshot) {
          hasChanged = true;
          break;
        }

        // Check if fields have changed
        if (currentSnapshot.fields.length !== prevSnapshot.fields.length) {
          hasChanged = true;
          break;
        }

        // Check if any field's submission has changed
        for (let i = 0; i < currentSnapshot.fields.length; i++) {
          const currentField = currentSnapshot.fields[i];
          const prevField = prevSnapshot.fields[i];
          
          if (currentField.id !== prevField.id) {
            hasChanged = true;
            break;
          }

          const currentSubmissionId = currentField.submission?.id;
          const prevSubmissionId = prevField.submission?.id;
          const currentSubmissionUpdated = currentField.submission?.updated_at;
          const prevSubmissionUpdated = prevField.submission?.updated_at;

          if (
            currentSubmissionId !== prevSubmissionId ||
            currentSubmissionUpdated !== prevSubmissionUpdated
          ) {
            hasChanged = true;
            break;
          }
        }

        if (hasChanged) break;
      }
    }

    if (hasChanged) {
      const aggregatedData = aggregateFormData();
      
      // Update ref
      if (formDataRef) {
        formDataRef.current = aggregatedData;
      }

      // Call aggregated callback
      if (onFormDataChange) {
        onFormDataChange(aggregatedData);
      }

      // Call per-form callbacks for changed forms
      if (onFormChange) {
        for (const [formId, snapshot] of formDataMap.entries()) {
          const prevSnapshot = prevFormDataMapRef.current.get(formId);
          const formChanged =
            !prevSnapshot ||
            JSON.stringify(snapshot) !== JSON.stringify(prevSnapshot);
          
          if (formChanged) {
            onFormChange(formId, snapshot);
          }
        }
      }

      // Update previous state
      prevFormDataMapRef.current = new Map(formDataMap);
    }
  }, [formDataMap, aggregateFormData, onFormDataChange, onFormChange, formDataRef]);
};

